import { useCallback, useEffect, useMemo, useState } from 'react';
import { Pencil, Plus, RefreshCw, Tag, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { listCustomers } from '../api/customers';
import {
  createDiscount,
  deleteDiscount,
  listDiscounts,
  updateDiscount
} from '../api/discounts';
import { getErrorMessage } from '../api/response';
import Button from '../components/Button';
import FormField, { inputClassName } from '../components/FormField';
import Modal from '../components/Modal';
import PageHeader from '../components/PageHeader';
import StatusBadge from '../components/StatusBadge';
import Table from '../components/Table';
import { LoadingState } from '../components/PageState';
import { currency, dateTime, fromInputDateTime, toInputDateTime } from '../utils/format';

const initialForm = {
  name: '',
  code: '',
  description: '',
  scope: 'both',
  customer_id: '',
  discount_type: 'percentage',
  discount_value: '',
  usage_limit: '',
  per_customer_limit: '1',
  starts_at: '',
  expires_at: '',
  is_active: true
};

const scopeLabels = {
  shop: 'Tienda',
  clinic: 'Clinica',
  both: 'Tienda y clinica'
};

const typeLabels = {
  percentage: 'Porcentaje',
  fixed_amount: 'Monto fijo'
};

const Descuentos = () => {
  const [discounts, setDiscounts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [scope, setScope] = useState('');
  const [active, setActive] = useState('1');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm);

  const customerById = useMemo(
    () => new Map(customers.map((customer) => [Number(customer.id), customer])),
    [customers]
  );

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [discountData, customerData] = await Promise.all([
        listDiscounts({
          scope: scope || undefined,
          active: active === '' ? undefined : active,
          limit: 100
        }),
        listCustomers({ limit: 200 })
      ]);
      setDiscounts(discountData);
      setCustomers(customerData);
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudieron cargar los descuentos'));
    } finally {
      setLoading(false);
    }
  }, [active, scope]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreate = () => {
    setEditing(null);
    setForm(initialForm);
    setModalOpen(true);
  };

  const openEdit = (discount) => {
    setEditing(discount);
    setForm({
      name: discount.name || '',
      code: discount.code || '',
      description: discount.description || '',
      scope: discount.scope || 'both',
      customer_id: discount.customer_id || '',
      discount_type: discount.discount_type || 'percentage',
      discount_value: discount.discount_value || '',
      usage_limit: discount.usage_limit || '',
      per_customer_limit: discount.per_customer_limit || '',
      starts_at: toInputDateTime(discount.starts_at),
      expires_at: toInputDateTime(discount.expires_at),
      is_active: Number(discount.is_active) === 1
    });
    setModalOpen(true);
  };

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const buildPayload = () => ({
    name: form.name.trim(),
    code: form.code.trim() || null,
    description: form.description.trim() || null,
    scope: form.scope,
    customer_id: form.customer_id || null,
    discount_type: form.discount_type,
    discount_value: Number(form.discount_value || 0),
    usage_limit: form.usage_limit || null,
    per_customer_limit: form.per_customer_limit || null,
    starts_at: fromInputDateTime(form.starts_at) || null,
    expires_at: fromInputDateTime(form.expires_at) || null,
    is_active: form.is_active
  });

  const saveDiscount = async () => {
    if (!form.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    if (!Number(form.discount_value || 0)) {
      toast.error('El valor del descuento debe ser mayor a cero');
      return;
    }

    try {
      setSaving(true);
      const payload = buildPayload();
      if (editing) {
        await updateDiscount(editing.id, payload);
        toast.success('Descuento actualizado');
      } else {
        await createDiscount(payload);
        toast.success('Descuento creado');
      }
      setModalOpen(false);
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo guardar el descuento'));
    } finally {
      setSaving(false);
    }
  };

  const disableDiscount = async (discount) => {
    if (!window.confirm(`Desactivar ${discount.name}?`)) return;

    try {
      await deleteDiscount(discount.id);
      toast.success('Descuento desactivado');
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo desactivar el descuento'));
    }
  };

  const activeDiscounts = discounts.filter((discount) => Number(discount.is_active) === 1);
  const customerDiscounts = discounts.filter((discount) => discount.customer_id);

  const columns = [
    {
      key: 'name',
      label: 'Descuento',
      render: (row) => (
        <div>
          <p className="font-semibold text-gray-900">{row.name}</p>
          <p className="text-xs text-gray-500">{row.code || 'Sin codigo'}</p>
        </div>
      )
    },
    {
      key: 'scope',
      label: 'Uso',
      render: (row) => scopeLabels[row.scope] || row.scope
    },
    {
      key: 'customer_id',
      label: 'Cliente',
      render: (row) => row.customer_id
        ? customerById.get(Number(row.customer_id))?.full_name || `#${row.customer_id}`
        : 'Todos'
    },
    {
      key: 'discount_value',
      label: 'Valor',
      render: (row) => row.discount_type === 'percentage'
        ? `${Number(row.discount_value || 0)}%`
        : currency(row.discount_value)
    },
    {
      key: 'limits',
      label: 'Uso',
      render: (row) => (
        <div className="text-xs text-gray-600">
          <p>Global: {row.usage_limit || 'Sin limite'}</p>
          <p>Por cliente: {row.per_customer_limit || 'Sin limite'}</p>
        </div>
      )
    },
    {
      key: 'expires_at',
      label: 'Vigencia',
      render: (row) => row.expires_at ? dateTime(row.expires_at) : 'Sin caducidad'
    },
    {
      key: 'is_active',
      label: 'Estado',
      render: (row) => Number(row.is_active) === 1
        ? <StatusBadge label="Activo" color="green" />
        : <StatusBadge label="Inactivo" color="gray" />
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (row) => (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => openEdit(row)}
            className="rounded-md p-2 text-blue-700 hover:bg-blue-50"
            title="Editar"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => disableDiscount(row)}
            className="rounded-md p-2 text-red-700 hover:bg-red-50"
            title="Desactivar"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Descuentos"
        description="Cupones y beneficios para tienda, servicios clinicos o clientes especificos."
        actions={<Button icon={Plus} onClick={openCreate}>Nuevo descuento</Button>}
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <Tag className="h-5 w-5 text-blue-600" />
          <p className="mt-2 text-sm text-gray-500">Activos</p>
          <p className="text-2xl font-bold text-gray-950">{activeDiscounts.length}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-500">Personalizados</p>
          <p className="mt-2 text-2xl font-bold text-gray-950">{customerDiscounts.length}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-500">Disponibles para todos</p>
          <p className="mt-2 text-2xl font-bold text-gray-950">{discounts.length - customerDiscounts.length}</p>
        </div>
      </div>

      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="grid gap-3 md:grid-cols-4">
          <FormField label="Alcance">
            <select className={inputClassName} value={scope} onChange={(event) => setScope(event.target.value)}>
              <option value="">Todos</option>
              <option value="shop">Tienda</option>
              <option value="clinic">Clinica</option>
              <option value="both">Tienda y clinica</option>
            </select>
          </FormField>
          <FormField label="Estado">
            <select className={inputClassName} value={active} onChange={(event) => setActive(event.target.value)}>
              <option value="">Todos</option>
              <option value="1">Activos</option>
              <option value="0">Inactivos</option>
            </select>
          </FormField>
          <div className="flex items-end md:col-span-2">
            <Button icon={RefreshCw} variant="secondary" onClick={loadData} className="w-full md:w-auto">
              Actualizar
            </Button>
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white">
        {loading ? (
          <div className="p-5"><LoadingState rows={5} /></div>
        ) : (
          <Table columns={columns} data={discounts} emptyMessage="No hay descuentos configurados" />
        )}
      </section>

      <Modal
        open={modalOpen}
        title={editing ? 'Editar descuento' : 'Nuevo descuento'}
        onClose={() => setModalOpen(false)}
        size="lg"
        footer={
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button type="button" onClick={saveDiscount} disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar descuento'}
            </Button>
          </div>
        }
      >
        <div className="grid gap-4 md:grid-cols-2">
          <FormField label="Nombre">
            <input className={inputClassName} value={form.name} onChange={(event) => updateField('name', event.target.value)} />
          </FormField>
          <FormField label="Codigo opcional">
            <input className={inputClassName} value={form.code} onChange={(event) => updateField('code', event.target.value)} placeholder="BIENVENIDA10" />
          </FormField>
          <FormField label="Alcance">
            <select className={inputClassName} value={form.scope} onChange={(event) => updateField('scope', event.target.value)}>
              <option value="shop">Solo tienda</option>
              <option value="clinic">Solo clinica</option>
              <option value="both">Tienda y clinica</option>
            </select>
          </FormField>
          <FormField label="Cliente">
            <select className={inputClassName} value={form.customer_id} onChange={(event) => updateField('customer_id', event.target.value)}>
              <option value="">Todos los clientes</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>{customer.full_name}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Tipo">
            <select className={inputClassName} value={form.discount_type} onChange={(event) => updateField('discount_type', event.target.value)}>
              <option value="percentage">Porcentaje</option>
              <option value="fixed_amount">Monto fijo</option>
            </select>
          </FormField>
          <FormField label={typeLabels[form.discount_type] || 'Valor'}>
            <input
              className={inputClassName}
              type="number"
              min="0"
              value={form.discount_value}
              onChange={(event) => updateField('discount_value', event.target.value)}
            />
          </FormField>
          <FormField label="Uso global">
            <input
              className={inputClassName}
              type="number"
              min="1"
              value={form.usage_limit}
              onChange={(event) => updateField('usage_limit', event.target.value)}
              placeholder="Sin limite"
            />
          </FormField>
          <FormField label="Uso por cliente">
            <input
              className={inputClassName}
              type="number"
              min="1"
              value={form.per_customer_limit}
              onChange={(event) => updateField('per_customer_limit', event.target.value)}
              placeholder="Sin limite"
            />
          </FormField>
          <FormField label="Inicio">
            <input className={inputClassName} type="datetime-local" value={form.starts_at} onChange={(event) => updateField('starts_at', event.target.value)} />
          </FormField>
          <FormField label="Caducidad">
            <input className={inputClassName} type="datetime-local" value={form.expires_at} onChange={(event) => updateField('expires_at', event.target.value)} />
          </FormField>
          <div className="md:col-span-2">
            <FormField label="Descripcion">
              <textarea
                className={inputClassName}
                rows={3}
                value={form.description}
                onChange={(event) => updateField('description', event.target.value)}
              />
            </FormField>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(event) => updateField('is_active', event.target.checked)}
            />
            Descuento activo
          </label>
        </div>
      </Modal>
    </div>
  );
};

export default Descuentos;
